<script setup>
import { useHoneybadger } from '@honeybadger-io/vue';
import { useClipboard, useShare } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { computed, ref } from 'vue';
import { findHeldPost } from '@/models/posts/postFeedModels.js';

const props = defineProps({
  postId: {
    type: [String, Number],
    required: true,
  },
  content: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['share-success']);
const honeybadger = useHoneybadger();

// Constants
const MAX_FILE_SIZE = 8 * 1024 * 1024;
const MAX_TOTAL_SIZE = 30 * 1024 * 1024;
const FETCH_TIMEOUT = 15000;
const MAX_IMAGES = 10;

// Data — from whichever table holds the post: a card on the bookmarks tab or
// the تدبيسة page reads its own table, and `Post` may never have seen it.
const post = computed(() => findHeldPost(props.postId, (repo) => repo.with('media')));

const { copy } = useClipboard();
const { isSupported: canShare, share: vueUseShare } = useShare();

const showImageSelector = ref(false);
const selectedImages = ref([]);
const preparedFiles = ref([]); // Pre-fetched files ready for sharing
const isPreparing = ref(false);
const isSharing = ref(false);
const preparationError = ref(null);

// Computed
const availableImages = computed(() => {
  if (!post.value?.media || !Array.isArray(post.value.media)) return [];

  return post.value.media
    .filter(item => item.mediaType === 'image')
    .slice(0, MAX_IMAGES)
    .map((item, index) => ({
      id: index,
      url: item.url,
    }));
});

const hasImages = computed(() => availableImages.value.length > 0);
const postContent = computed(() => props.content || post.value?.content || '');
const isReady = computed(() => preparedFiles.value.length > 0 && !isPreparing.value);

// Helper: Show toast
const showToast = (text, duration = 2000) => {
  f7.toast.create({
    text,
    position: 'center',
    closeTimeout: duration,
  }).open();
};

const showError = (text) => {
  f7.toast.create({
    text,
    position: 'center',
    closeTimeout: 4000,
    cssClass: 'toast-error',
  }).open();
};

// Helper: Compress image
async function compressImage(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timeoutId = setTimeout(() => {
      reject(new Error('Compression timeout'));
      URL.revokeObjectURL(img.src);
    }, 10000);

    img.onload = () => {
      clearTimeout(timeoutId);

      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Canvas context not available');
        }

        const maxDimension = 1920;
        let { width, height } = img;

        if (width > maxDimension || height > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (compressedBlob) => {
            URL.revokeObjectURL(img.src);
            if (compressedBlob && compressedBlob.size < blob.size) {
              resolve(compressedBlob);
            } else {
              resolve(blob);
            }
          },
          'image/jpeg',
          0.85,
        );
      } catch (error) {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(img.src);
        reject(error);
      }
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(img.src);
      reject(new Error('Image load failed'));
    };

    img.src = URL.createObjectURL(blob);
  });
}

// CRITICAL: Pre-fetch and prepare files BEFORE sharing
// This runs independently of the share gesture, avoiding timeout issues
async function prepareFilesForSharing(imageUrls) {
  const files = [];
  let totalSize = 0;
  const failedImages = [];

  for (let i = 0; i < imageUrls.length; i++) {
    let success = false;
    let lastError = null;

    for (let attempt = 0; attempt < 3 && !success; attempt++) {
      try {
        const url = imageUrls[i];
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
          cache: attempt === 0 ? 'force-cache' : attempt === 1 ? 'no-cache' : 'reload',
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        let blob = await response.blob();

        if (!blob || blob.size === 0) {
          throw new Error('Empty blob');
        }

        // Compress if needed
        if (blob.size > MAX_FILE_SIZE) {
          try {
            blob = await compressImage(blob);
          } catch (compressError) {
            console.warn(`Image ${i} compression failed:`, compressError);
          }
        }

        // Check total size
        if (totalSize + blob.size > MAX_TOTAL_SIZE) {
          console.warn(`Image ${i} skipped - size limit`);
          failedImages.push({ index: i, reason: 'size_limit' });
          break;
        }

        totalSize += blob.size;

        // Ensure correct MIME type
        let mimeType = blob.type;
        if (!mimeType || !mimeType.startsWith('image/')) {
          mimeType = url.match(/\.(jpg|jpeg)$/i)
            ? 'image/jpeg'
            : url.match(/\.png$/i)
              ? 'image/png'
              : url.match(/\.webp$/i)
                ? 'image/webp'
                : 'image/jpeg';
        }

        const typedBlob = new Blob([blob], { type: mimeType });
        const file = new File([typedBlob], `image-${i + 1}.jpg`, {
          type: mimeType,
          lastModified: Date.now(),
        });

        files.push(file);
        success = true;
      } catch (error) {
        lastError = error;
        if (attempt < 2) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    if (!success) {
      failedImages.push({
        index: i,
        reason: 'fetch_failed',
        error: lastError?.message,
      });
    }
  }

  if (files.length === 0) {
    throw new Error('فشل تحميل جميع الصور');
  }

  return { files, failedCount: failedImages.length, totalCount: imageUrls.length };
}

// Step 1: Open selector and start preparing files
const openImageSelector = () => {
  if (!hasImages.value) {
    showToast('لا توجد صور متاحة للمشاركة');
    return;
  }
  if (!canShare.value) {
    showToast('المشاركة غير مدعومة على هذا الجهاز');
    return;
  }

  // Reset state
  selectedImages.value = availableImages.value.map(img => img.id);
  preparedFiles.value = [];
  preparationError.value = null;

  showImageSelector.value = true;
};

const closeImageSelector = () => {
  showImageSelector.value = false;
  if (!isSharing.value) {
    selectedImages.value = [];
    preparedFiles.value = [];
    preparationError.value = null;
  }
};

const toggleImageSelection = (imageId) => {
  const index = selectedImages.value.indexOf(imageId);
  if (index > -1) {
    selectedImages.value.splice(index, 1);
  } else {
    selectedImages.value.push(imageId);
  }

  // Clear prepared files when selection changes
  preparedFiles.value = [];
  preparationError.value = null;
};

const selectAllImages = () => {
  selectedImages.value = availableImages.value.map(img => img.id);
  preparedFiles.value = [];
  preparationError.value = null;
};

const deselectAllImages = () => {
  selectedImages.value = [];
  preparedFiles.value = [];
  preparationError.value = null;
};

// Step 2: Prepare files (happens BEFORE share button is enabled)
const handlePrepareFiles = async () => {
  if (selectedImages.value.length === 0) {
    showToast('اختار صورة واحدة على الأقل');
    return;
  }

  isPreparing.value = true;
  preparationError.value = null;

  try {
    const imageUrls = availableImages.value
      .filter(img => selectedImages.value.includes(img.id))
      .map(img => img.url);

    showToast(
      `جاري تحضير ${imageUrls.length} ${imageUrls.length === 1 ? 'صورة' : 'صور'}...`,
      2000,
    );

    const result = await prepareFilesForSharing(imageUrls);

    preparedFiles.value = result.files;

    if (result.failedCount > 0) {
      showToast(
        `تم تحضير ${result.files.length} من ${result.totalCount} صور`,
        3000,
      );
    } else {
      showToast('تم تحضير الصور بنجاح! اضغط "مشاركة" للمتابعة', 3000);
    }
  } catch (error) {
    preparationError.value = error.message;

    let errorMessage = 'فشل تحضير الصور';

    if (error.message.includes('فشل تحميل')) {
      errorMessage = 'فشل تحميل الصور. تحقق من اتصال الإنترنت وحاول مرة أخرى';
    }

    showError(errorMessage);

    console.error('File preparation failed:', error);
    honeybadger.notify(error);
  } finally {
    isPreparing.value = false;
  }
};

// Step 3: Execute share with pre-prepared files (INSTANT - no async delay)
const executeShare = async () => {
  if (preparedFiles.value.length === 0) {
    showToast('حضّر الصور الأول');
    return;
  }

  if (!canShare.value) {
    showToast('المشاركة غير مدعومة على هذا الجهاز');
    return;
  }

  isSharing.value = true;

  try {
    // Prepare share data - FILES ONLY
    const shareData = { files: preparedFiles.value };

    // Verify files can be shared
    if (!navigator.canShare || !navigator.canShare(shareData)) {
      showError('لا يمكن مشاركة هذه الملفات على جهازك');
      return;
    }

    // Copy text to clipboard (independent operation)
    if (postContent.value) {
      try {
        await copy(postContent.value);
      } catch (copyError) {
        console.warn('Failed to copy text:', copyError);
      }
    }

    // CRITICAL: Share immediately with prepared files - NO async operations
    await vueUseShare(shareData);

    // Success
    let successMessage = 'تمت المشاركة بنجاح';
    if (postContent.value) {
      successMessage = 'تمت المشاركة! النص منسوخ - الصقه مع الصور';
    }

    showToast(successMessage, 4000);

    emit('share-success', {
      images: preparedFiles.value.length,
      total: selectedImages.value.length,
    });

    // Clean up
    showImageSelector.value = false;
    selectedImages.value = [];
    preparedFiles.value = [];
    preparationError.value = null;
  } catch (error) {
    // User cancellation is not an error
    if (error.name === 'AbortError') {
      console.log('User cancelled share');
      return;
    }

    // Handle errors
    let errorMessage = 'حدث خطأ أثناء المشاركة';

    if (error.name === 'NotAllowedError') {
      errorMessage = 'تم رفض الإذن للمشاركة. حاول مرة أخرى';
    } else if (error.name === 'DataError') {
      errorMessage = 'الملفات كبيرة جداً. حاول تقليل عدد الصور';
    }

    showError(errorMessage);

    console.error('Share failed:', {
      name: error.name,
      message: error.message,
      fileCount: preparedFiles.value.length,
    });
    honeybadger.notify(error);
  } finally {
    isSharing.value = false;
  }
};
</script>

<template>
  <div class="platform-share">
    <f7-button
      round
      class="share-btn"
      icon-f7="square_arrow_up"
      :disabled="!hasImages || !canShare"
      @click="openImageSelector"
    ></f7-button>

    <f7-popup
      class="image-selector-popup"
      :opened="showImageSelector"
      @popup:closed="closeImageSelector"
    >
      <f7-page>
        <f7-navbar title="اختر الصور للمشاركة">
          <f7-nav-right>
            <f7-link popup-close>إغلاق</f7-link>
          </f7-nav-right>
        </f7-navbar>

        <f7-toolbar bottom>
          <f7-link @click="selectAllImages">اختيار الكل</f7-link>
          <f7-link @click="deselectAllImages">إلغاء الاختيار</f7-link>
          <f7-link>
            <f7-badge :color="selectedImages.length > 0 ? 'green' : 'gray'">
              {{ selectedImages.length }} / {{ availableImages.length }}
            </f7-badge>
          </f7-link>
        </f7-toolbar>

        <f7-block-title>اختر الصور التي تريد مشاركتها</f7-block-title>

        <f7-block>
          <div class="images-selection-grid">
            <div
              v-for="image in availableImages"
              :key="image.id"
              class="image-selection-item"
              :class="{ selected: selectedImages.includes(image.id) }"
              @click="toggleImageSelection(image.id)"
            >
              <img :src="image.url" :alt="`صورة ${image.id + 1}`" />
              <div v-if="selectedImages.includes(image.id)" class="selection-badge">
                <f7-icon f7="checkmark_circle_fill" color="green" size="28"></f7-icon>
              </div>
            </div>
          </div>
        </f7-block>

        <f7-block v-if="postContent" class="text-preview">
          <f7-block-title>النص (هيتنسخ تلقائياً)</f7-block-title>
          <div class="text-content">{{ postContent }}</div>
        </f7-block>

        <!-- Step 1: Prepare files button -->
        <f7-block v-if="!isReady && !preparationError">
          <f7-button
            fill
            large
            color="orange"
            :disabled="selectedImages.length === 0 || isPreparing"
            :preloader="isPreparing"
            :loading="isPreparing"
            @click="handlePrepareFiles"
          >
            {{ isPreparing ? 'جاري التحضير...' : 'تحضير الصور' }}
          </f7-button>
          <div class="info-text">
            حضّر الصور الأول، وبعدين اضغط مشاركة
          </div>
        </f7-block>

        <!-- Error state -->
        <f7-block v-if="preparationError">
          <div class="error-message">
            <f7-icon f7="exclamationmark_triangle_fill" color="red" size="24"></f7-icon>
            <span>{{ preparationError }}</span>
          </div>
          <f7-button
            fill
            large
            color="orange"
            @click="handlePrepareFiles"
          >
            إعادة المحاولة
          </f7-button>
        </f7-block>

        <!-- Step 2: Share button (only enabled after preparation) -->
        <f7-block v-if="isReady">
          <div class="success-message">
            <f7-icon f7="checkmark_circle_fill" color="green" size="24"></f7-icon>
            <span>تم تحضير {{ preparedFiles.length }} صورة</span>
          </div>
          <f7-button
            fill
            large
            color="green"
            :disabled="isSharing"
            :preloader="isSharing"
            :loading="isSharing"
            @click="executeShare"
          >
            {{ isSharing ? 'جاري المشاركة...' : 'مشاركة الآن' }}
          </f7-button>
        </f7-block>
      </f7-page>
    </f7-popup>
  </div>
</template>

<style scoped>
.platform-share {
    display: inline-block;
}

.share-btn {
    --f7-button-height: 40px;
    --f7-button-width: 40px;
    --f7-button-padding-horizontal: 0;
    --f7-button-border-width: 1px;
    --f7-button-border-color: #e5e7eb;
    --f7-button-bg-color: #ffffff;
    --f7-button-text-color: #6b7280;
    transition: all 0.2s ease;
}

.share-btn:active {
    transform: scale(0.95);
}

.share-btn:hover {
    transform: scale(1.05);
    --f7-button-bg-color: #f9fafb;
}

.images-selection-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 12px;
    padding: 0;
}

.image-selection-item {
    position: relative;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 3px solid transparent;
    transition: all 0.2s ease;
}

.image-selection-item.selected {
    border-color: var(--f7-theme-color);
    transform: scale(0.95);
}

.image-selection-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.selection-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    background: white;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.text-preview {
    background: #f9fafb;
    border-radius: 8px;
    padding: 12px;
    margin: 16px 0;
}

.text-content {
    font-size: 14px;
    line-height: 1.6;
    color: #374151;
    max-height: 150px;
    overflow-y: auto;
}

.info-text {
    text-align: center;
    margin-top: 12px;
    font-size: 13px;
    color: #6b7280;
}

.success-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    background: #ecfdf5;
    border-radius: 8px;
    margin-bottom: 16px;
    font-weight: 500;
    color: #065f46;
}

.error-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    background: #fef2f2;
    border-radius: 8px;
    margin-bottom: 16px;
    font-weight: 500;
    color: #991b1b;
}

.dark .share-btn {
    --f7-button-bg-color: #1f2937;
    --f7-button-border-color: #374151;
    --f7-button-text-color: #9ca3af;
}

.dark .share-btn:hover {
    --f7-button-bg-color: #111827;
}

.dark .text-preview {
    background: #1f2937;
}

.dark .text-content {
    color: #d1d5db;
}

.dark .success-message {
    background: #064e3b;
    color: #d1fae5;
}

.dark .error-message {
    background: #7f1d1d;
    color: #fecaca;
}
</style>
